import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme.jsx';
import { useI18n } from '../../i18n/i18n.jsx';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import ContactFormModal from '../UI/ContactFormModal';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { theme } = useTheme();
  const { t } = useI18n();
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/about', label: t('nav.about') },
    { to: '/team', label: t('nav.team') },
    { to: '/services', label: t('nav.services') },
    { to: '/contact', label: t('nav.contact') },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/80 dark:bg-neutral-900/80 backdrop-blur-xl shadow-lg'
          : 'bg-white dark:bg-neutral-900'
      } border-b border-neutral-200 dark:border-neutral-800 smooth-transition`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 group"
            aria-label="Bereketli Dowur - Home"
          >
            <div className="flex justify-center items-center w-full py-6"> 
  <img 
    src="/logo.png" 
    alt="Bereketli Dowur" 
    className="w-64 h-auto md:w-80 lg:w-[400px] rounded-lg transition-all duration-500 ease-in-out hover:scale-105 object-contain"
  />
</div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="group relative px-3 py-2 text-sm font-medium transition-all duration-300 whitespace-nowrap"
              >
                <span className={`${
                  location.pathname === link.to
                    ? 'text-accent'
                    : 'text-neutral-700 dark:text-neutral-300 group-hover:text-primary dark:group-hover:text-accent'
                } transition-colors duration-300`}>
                  {link.label}
                </span>
                <span className={`absolute bottom-0 left-0 h-0.5 bg-accent rounded-full transition-all duration-300 ${
                  location.pathname === link.to ? 'w-full' : 'w-0 group-hover:w-full'
                }`} />
              </Link>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageSwitcher />
            <ThemeToggle />
            <button
              onClick={() => setIsModalOpen(true)}
              className="group relative px-5 py-2 bg-primary text-white text-sm font-semibold rounded-lg overflow-hidden hover-lift shine-on-hover shadow-lg"
            >
              <span className="relative z-10">{t('hero.cta')}</span>
              <div className="absolute inset-0 bg-primary-light opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </button>
          </div>
        </div>
      </div>

      {/* Contact Form Modal */}
      <ContactFormModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </nav>
  );
};

export default Navbar;